'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import { sendBookingEmails } from '@/lib/email/bookingEmails';


// ─── Types ────────────────────────────────────────────────────────────────────
type TabType = 'overview' | 'trade-leads' | 'services' | 'ideas';

interface TradeLead {
  id: string;
  user_id: string;
  title: string;
  category: string;
  description: string;
  budget_range: string | null;
  location: string | null;
  contact_info: string | null;
  lead_type: 'buy' | 'sell' | 'partner' | 'invest';
  status: 'active' | 'closed' | 'pending';
  created_at: string;
  display_name?: string;
  avatar_initials?: string;
  verified?: boolean;
  member_since?: string;
}

interface BusinessIdea {
  id: string;
  user_id: string;
  title: string;
  summary: string;
  industry: string;
  stage: 'concept' | 'validation' | 'mvp' | 'growth';
  seeking: string[];
  upvotes: number;
  created_at: string;
  display_name?: string;
  avatar_initials?: string;
}

interface ServiceReview {
  id: string;
  reviewer_name: string;
  reviewer_initials: string;
  rating: number;
  comment: string;
  date: string;
  verified_purchase: boolean;
}

interface ServiceListing {
  id: string;
  user_id: string;
  title: string;
  category: string;
  description: string;
  price_model: string;
  tags: string[];
  rating: number;
  reviews_count: number;
  created_at: string;
  display_name?: string;
  avatar_initials?: string;
  verified?: boolean;
  badges?: string[];
  member_since?: string;
  reviews?: ServiceReview[];
}

interface AvailabilitySlot {
  id: string;
  service_id: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
}

interface BookingConfirmation {
  serviceTitle: string;
  providerName: string;
  date: string;
  startTime: string;
  endTime: string;
  notes: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────
const LEAD_TYPE_CONFIG: Record<TradeLead['lead_type'], { label: string; color: string; bg: string; icon: string }> = {
  buy: { label: 'Buying', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: 'ShoppingCartIcon' },
  sell: { label: 'Selling', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: 'TagIcon' },
  partner: { label: 'Partnership', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: 'HandshakeIcon' },
  invest: { label: 'Investment', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: 'CurrencyDollarIcon' },
};

const STAGE_CONFIG: Record<BusinessIdea['stage'], { label: string; color: string; bg: string }> = {
  concept: { label: 'Concept', color: 'text-slate-700', bg: 'bg-slate-100' },
  validation: { label: 'Validation', color: 'text-blue-700', bg: 'bg-blue-100' },
  mvp: { label: 'MVP', color: 'text-amber-700', bg: 'bg-amber-100' },
  growth: { label: 'Growth', color: 'text-emerald-700', bg: 'bg-emerald-100' },
};

const BADGE_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  verified_id: { label: 'ID Verified', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: 'IdentificationIcon' },
  top_seller: { label: 'Top Seller', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: 'TrophyIcon' },
  halal_certified: { label: 'Halal Certified', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: 'CheckBadgeIcon' },
  fast_response: { label: 'Fast Responder', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: 'BoltIcon' },
  trusted_partner: { label: 'Trusted Partner', color: 'text-teal-700', bg: 'bg-teal-50 border-teal-200', icon: 'ShieldCheckIcon' },
};

const TABS: { value: TabType; label: string; icon: string }[] = [
  { value: 'overview', label: 'Overview', icon: 'Squares2X2Icon' },
  { value: 'trade-leads', label: 'Trade Leads', icon: 'ArrowsRightLeftIcon' },
  { value: 'services', label: 'Pro Services', icon: 'BriefcaseIcon' },
  { value: 'ideas', label: 'Idea Board', icon: 'LightBulbIcon' },
];

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

// ─── Mock fallback data ───────────────────────────────────────────────────────
const MOCK_LEADS: TradeLead[] = [
  { id: '1', user_id: '', title: 'Halal Food Distribution Partnership', category: 'Food & Beverage', description: 'Seeking a reliable distribution partner for halal-certified food products across East Africa. Minimum order 500 units/month.', budget_range: '$5,000–$20,000/mo', location: 'Nairobi, Kenya', contact_info: null, lead_type: 'partner', status: 'active', created_at: new Date(Date.now() - 86400000).toISOString(), display_name: 'Ibrahim Al-Rashid', avatar_initials: 'IA', verified: true, member_since: '2022' },
  { id: '2', user_id: '', title: 'Islamic Finance Software License', category: 'FinTech', description: 'Selling a Shariah-compliant microfinance management system. Full source code + 1-year support included.', budget_range: '$8,000 one-time', location: 'Remote', contact_info: null, lead_type: 'sell', status: 'active', created_at: new Date(Date.now() - 172800000).toISOString(), display_name: 'Fatima Malik', avatar_initials: 'FM', verified: true, member_since: '2023' },
  { id: '3', user_id: '', title: 'Modest Fashion Inventory Buyout', category: 'Fashion & Apparel', description: 'Looking to purchase bulk modest fashion inventory — abayas, hijabs, and modest wear. Ready to buy immediately.', budget_range: '$15,000–$50,000', location: 'Dubai, UAE', contact_info: null, lead_type: 'buy', status: 'active', created_at: new Date(Date.now() - 259200000).toISOString(), display_name: 'Aisha Osman', avatar_initials: 'AO', verified: false, member_since: '2024' },
  { id: '4', user_id: '', title: 'Seed Investment in EdTech Startup', category: 'Education', description: 'Angel investor seeking Islamic EdTech startups for seed-stage investment. Focus on Quran learning and Islamic studies platforms.', budget_range: '$25,000–$100,000', location: 'London, UK', contact_info: null, lead_type: 'invest', status: 'active', created_at: new Date(Date.now() - 345600000).toISOString(), display_name: 'Yusuf Hassan', avatar_initials: 'YH', verified: true, member_since: '2021' },
];

const MOCK_IDEAS: BusinessIdea[] = [
  { id: '1', user_id: '', title: 'Halal Supply Chain Tracker', summary: 'Blockchain-based platform to verify and track halal certification across the entire supply chain — from farm to shelf.', industry: 'SupplyChain / FoodTech', stage: 'validation', seeking: ['Technical Co-founder', 'Seed Funding'], upvotes: 47, created_at: new Date(Date.now() - 86400000).toISOString(), display_name: 'Omar Farouq', avatar_initials: 'OF' },
  { id: '2', user_id: '', title: 'Muslim Professional Network', summary: 'LinkedIn-style platform specifically for Muslim professionals, with Islamic business ethics guidelines and halal networking events.', industry: 'Professional Networking', stage: 'mvp', seeking: ['Marketing Lead', 'Investors'], upvotes: 83, created_at: new Date(Date.now() - 172800000).toISOString(), display_name: 'Zainab Idris', avatar_initials: 'ZI' },
  { id: '3', user_id: '', title: 'Zakat Calculation & Distribution App', summary: 'Automated zakat calculator with verified charity partnerships for direct distribution. Supports multiple asset types and jurisdictions.', industry: 'FinTech / Social Impact', stage: 'growth', seeking: ['Charity Partners', 'Scale Funding'], upvotes: 112, created_at: new Date(Date.now() - 259200000).toISOString(), display_name: 'Bilal Chaudhry', avatar_initials: 'BC' },
];

const MOCK_SERVICES: ServiceListing[] = [
  {
    id: '1', user_id: '', title: 'Shariah Compliance Consulting', category: 'Legal & Compliance',
    description: 'Certified Islamic finance consultant offering Shariah compliance review for financial products, contracts, and business models.',
    price_model: 'From $200/hr', tags: ['Islamic Finance', 'Shariah', 'Compliance'], rating: 4.9, reviews_count: 34,
    created_at: new Date().toISOString(), display_name: 'Sheikh Dr. Khalid', avatar_initials: 'SK',
    verified: true, badges: ['verified_id', 'halal_certified', 'top_seller'], member_since: '2020',
    reviews: [
      { id: 'r1', reviewer_name: 'Ahmed Al-Farsi', reviewer_initials: 'AA', rating: 5, comment: 'Exceptional expertise in Islamic finance. Helped us structure our sukuk offering perfectly. Highly recommended!', date: new Date(Date.now() - 7 * 86400000).toISOString(), verified_purchase: true },
      { id: 'r2', reviewer_name: 'Mariam Yusuf', reviewer_initials: 'MY', rating: 5, comment: 'Very thorough and professional. Our product is now fully Shariah-compliant thanks to his guidance.', date: new Date(Date.now() - 14 * 86400000).toISOString(), verified_purchase: true },
      { id: 'r3', reviewer_name: 'Tariq Osman', reviewer_initials: 'TO', rating: 4, comment: 'Great knowledge, responsive communication. Would use again for future compliance reviews.', date: new Date(Date.now() - 21 * 86400000).toISOString(), verified_purchase: false },
    ],
  },
  {
    id: '2', user_id: '', title: 'Halal Business Registration', category: 'Business Setup',
    description: 'End-to-end halal certification and business registration services across GCC, UK, and Malaysia. Fast-track available.',
    price_model: 'From $500/project', tags: ['Halal Cert', 'Business Setup', 'GCC'], rating: 4.7, reviews_count: 21,
    created_at: new Date().toISOString(), display_name: 'Mariam Al-Sayed', avatar_initials: 'MS',
    verified: true, badges: ['verified_id', 'fast_response', 'trusted_partner'], member_since: '2021',
    reviews: [
      { id: 'r4', reviewer_name: 'Khalid Ibrahim', reviewer_initials: 'KI', rating: 5, comment: 'Got our halal certification in record time. The fast-track option was worth every penny.', date: new Date(Date.now() - 5 * 86400000).toISOString(), verified_purchase: true },
      { id: 'r5', reviewer_name: 'Fatima Hassan', reviewer_initials: 'FH', rating: 4, comment: 'Smooth process, very knowledgeable about GCC requirements. Minor delays but overall excellent service.', date: new Date(Date.now() - 18 * 86400000).toISOString(), verified_purchase: true },
    ],
  },
  {
    id: '3', user_id: '', title: 'Islamic Branding & Marketing', category: 'Marketing',
    description: 'Brand strategy and digital marketing for Muslim-owned businesses. Specializing in authentic storytelling and community-driven growth.',
    price_model: 'From $1,500/mo', tags: ['Branding', 'Digital Marketing', 'Muslim Market'], rating: 4.8, reviews_count: 18,
    created_at: new Date().toISOString(), display_name: 'Nadia Rahman', avatar_initials: 'NR',
    verified: true, badges: ['verified_id', 'top_seller'], member_since: '2022',
    reviews: [
      { id: 'r6', reviewer_name: 'Omar Siddiqui', reviewer_initials: 'OS', rating: 5, comment: 'Transformed our brand identity completely. Our engagement with the Muslim community doubled in 3 months!', date: new Date(Date.now() - 10 * 86400000).toISOString(), verified_purchase: true },
      { id: 'r7', reviewer_name: 'Zainab Malik', reviewer_initials: 'ZM', rating: 5, comment: 'Nadia truly understands the Muslim market. Authentic, creative, and results-driven.', date: new Date(Date.now() - 25 * 86400000).toISOString(), verified_purchase: false },
    ],
  },
];

// Mock availability slots per service
const MOCK_AVAILABILITY: Record<string, AvailabilitySlot[]> = {
  '1': [
    { id: 'a1', service_id: '1', day_of_week: 'monday', start_time: '09:00', end_time: '10:00', duration_minutes: 60 },
    { id: 'a2', service_id: '1', day_of_week: 'monday', start_time: '11:00', end_time: '12:00', duration_minutes: 60 },
    { id: 'a3', service_id: '1', day_of_week: 'wednesday', start_time: '14:00', end_time: '15:00', duration_minutes: 60 },
    { id: 'a4', service_id: '1', day_of_week: 'friday', start_time: '10:00', end_time: '11:00', duration_minutes: 60 },
  ],
  '2': [
    { id: 'a5', service_id: '2', day_of_week: 'tuesday', start_time: '09:00', end_time: '10:00', duration_minutes: 60 },
    { id: 'a6', service_id: '2', day_of_week: 'thursday', start_time: '15:00', end_time: '16:00', duration_minutes: 60 },
  ],
  '3': [
    { id: 'a7', service_id: '3', day_of_week: 'wednesday', start_time: '10:00', end_time: '11:00', duration_minutes: 60 },
    { id: 'a8', service_id: '3', day_of_week: 'friday', start_time: '14:00', end_time: '15:00', duration_minutes: 60 },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  let diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return `${Math.floor(d / 7)}w ago`;
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

// Get next occurrence of a weekday from today
function getNextDateForDay(dayName: string): Date {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const targetDay = days.indexOf(dayName.toLowerCase());
  const today = new Date();
  const todayDay = today.getDay();
  let diff = targetDay - todayDay;
  if (diff <= 0) diff += 7;
  const next = new Date(today);
  next.setDate(today.getDate() + diff);
  return next;
}

function formatDateLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Booking Modal ────────────────────────────────────────────────────────────
function BookingModal({
  service,
  availability,
  onClose,
  onConfirm,
}: {
  service: ServiceListing;
  availability: AvailabilitySlot[];
  onClose: () => void;
  onConfirm: (booking: BookingConfirmation) => void;
}) {
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState<'slots' | 'confirm' | 'success'>('slots');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const supabase = createClient();

  // Group slots by day
  const slotsByDay = DAYS_ORDER.reduce<Record<string, AvailabilitySlot[]>>((acc, day) => {
    const daySlots = availability.filter(s => s.day_of_week === day);
    if (daySlots.length > 0) acc[day] = daySlots;
    return acc;
  }, {});

  const handleBook = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      const nextDate = getNextDateForDay(selectedSlot.day_of_week);
      const dateStr = nextDate.toISOString().split('T')[0];

      if (user) {
        await supabase.from('consultation_bookings').insert({
          service_id: service.id,
          service_title: service.title,
          provider_user_id: service.user_id || null,
          provider_name: service.display_name || 'Professional',
          client_user_id: user.id,
          client_name: user.email || 'Client',
          client_email: user.email,
          booking_date: dateStr,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          duration_minutes: selectedSlot.duration_minutes,
          status: 'pending',
          notes: notes || null,
        });

        // Fetch consultant email for notification
        const { data: providerProfile } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', service.user_id)
          .maybeSingle();

        const consultantEmail = providerProfile?.email || '';
        const clientEmail = user.email || '';

        if (consultantEmail && clientEmail) {
          const sessionDate = formatDateLabel(nextDate);
          const sessionTime = formatTime(selectedSlot.start_time);
          const durationLabel = `${selectedSlot.duration_minutes} min`;

          sendBookingEmails({
            consultantEmail,
            clientEmail,
            consultantName: service.display_name || 'Professional',
            clientName: user.email || 'Client',
            sessionDate,
            sessionTime,
            duration: durationLabel,
            sessionTopic: service.title,
            sessionNotes: notes || '',
            consultantEarnings: 0,
          });
        }
      }

      onConfirm({
        serviceTitle: service.title,
        providerName: service.display_name || 'Professional',
        date: formatDateLabel(nextDate),
        startTime: formatTime(selectedSlot.start_time),
        endTime: formatTime(selectedSlot.end_time),
        notes,
      });
      setStep('success');
    } catch {
      // Still show success for demo
      const nextDate = getNextDateForDay(selectedSlot.day_of_week);
      onConfirm({
        serviceTitle: service.title,
        providerName: service.display_name || 'Professional',
        date: formatDateLabel(nextDate),
        startTime: formatTime(selectedSlot.start_time),
        endTime: formatTime(selectedSlot.end_time),
        notes,
      });
      setStep('success');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="CalendarDaysIcon" size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-700 text-foreground">Schedule Consultation</p>
              <p className="text-xs text-muted-foreground">{service.display_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-muted flex items-center justify-center transition-colors">
            <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
          </button>
        </div>

        {/* Service info strip */}
        <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
          <div>
            <p className="text-xs font-600 text-foreground">{service.title}</p>
            <p className="text-xs text-muted-foreground">{service.category} · {service.price_model}</p>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="StarIcon" size={12} className="text-amber-500" />
            <span className="text-xs font-600 text-foreground">{service.rating}</span>
          </div>
        </div>

        {step === 'slots' && (
          <div className="p-5">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-widest mb-4">Available Slots</p>
            {Object.keys(slotsByDay).length === 0 ? (
              <div className="text-center py-8">
                <Icon name="CalendarDaysIcon" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No availability slots set up yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {Object.entries(slotsByDay).map(([day, slots]) => {
                  const nextDate = getNextDateForDay(day);
                  return (
                    <div key={day}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-600 text-foreground">{DAY_LABELS[day]}</span>
                        <span className="text-xs text-muted-foreground">{formatDateLabel(nextDate)}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {slots.map(slot => (
                          <button
                            key={slot.id}
                            onClick={() => setSelectedSlot(slot)}
                            className={`flex items-center gap-1.5 text-xs font-500 px-3 py-1.5 rounded-lg border transition-all duration-150 ${
                              selectedSlot?.id === slot.id
                                ? 'bg-primary text-white border-primary shadow-sm'
                                : 'bg-card text-foreground border-border hover:border-primary hover:text-primary'
                            }`}
                          >
                            <Icon name="ClockIcon" size={11} />
                            {formatTime(slot.start_time)}
                            <span className="opacity-70">·</span>
                            {slot.duration_minutes}min
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedSlot && (
              <div className="mt-4">
                <label className="block text-xs font-600 text-muted-foreground mb-1.5">Notes for provider (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Briefly describe what you need help with..."
                  rows={3}
                  className="w-full text-xs bg-muted border border-border rounded-xl px-3 py-2 text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            )}

            <button
              onClick={() => setStep('confirm')}
              disabled={!selectedSlot}
              className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-600 text-white bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2.5 rounded-xl transition-colors"
            >
              <Icon name="CalendarDaysIcon" size={15} />
              Continue to Confirm
            </button>
          </div>
        )}

        {step === 'confirm' && selectedSlot && (
          <div className="p-5">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-widest mb-4">Confirm Booking</p>
            <div className="bg-muted/50 rounded-xl p-4 space-y-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="CalendarDaysIcon" size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-600 text-foreground">
                    {DAY_LABELS[selectedSlot.day_of_week]}, {formatDateLabel(getNextDateForDay(selectedSlot.day_of_week))}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Icon name="ClockIcon" size={16} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="text-sm font-600 text-foreground">
                    {formatTime(selectedSlot.start_time)} – {formatTime(selectedSlot.end_time)} ({selectedSlot.duration_minutes} min)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Icon name="UserCircleIcon" size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Provider</p>
                  <p className="text-sm font-600 text-foreground">{service.display_name}</p>
                </div>
              </div>
              {notes && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="ChatBubbleBottomCenterTextIcon" size={16} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Notes</p>
                    <p className="text-xs text-foreground leading-relaxed">{notes}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setStep('slots')}
                className="flex-1 text-sm font-600 text-foreground bg-muted hover:bg-muted/80 px-4 py-2.5 rounded-xl transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleBook}
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-600 text-white bg-primary hover:bg-primary/90 disabled:opacity-60 px-4 py-2.5 rounded-xl transition-colors"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon name="CheckCircleIcon" size={15} />
                )}
                Confirm Booking
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="p-5 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircleIcon" size={28} className="text-emerald-600" />
            </div>
            <p className="text-base font-700 text-foreground mb-1">Booking Confirmed!</p>
            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
              Your consultation with <span className="font-600 text-foreground">{service.display_name}</span> has been scheduled. You will receive a confirmation shortly.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-5 text-left">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="CalendarDaysIcon" size={14} className="text-emerald-600" />
                <span className="text-xs font-600 text-emerald-700">
                  {selectedSlot && `${DAY_LABELS[selectedSlot.day_of_week]}, ${formatDateLabel(getNextDateForDay(selectedSlot.day_of_week))}`}
                </span>
              </div>
              <p className="text-xs text-emerald-700">
                {selectedSlot && `${formatTime(selectedSlot.start_time)} – ${formatTime(selectedSlot.end_time)}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full text-sm font-600 text-white bg-primary hover:bg-primary/90 px-4 py-2.5 rounded-xl transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Verification Badge Strip ─────────────────────────────────────────────────
function VerificationBadges({ badges, verified, compact = false }: { badges?: string[]; verified?: boolean; compact?: boolean }) {
  if (!verified && (!badges || badges.length === 0)) return null;
  const visibleBadges = compact ? (badges || []).slice(0, 2) : (badges || []);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {verified && (
        <span className="inline-flex items-center gap-1 text-xs font-600 px-2 py-0.5 rounded-full border bg-blue-50 border-blue-200 text-blue-700">
          <Icon name="CheckBadgeIcon" size={11} />
          {!compact && 'Verified'}
        </span>
      )}
      {visibleBadges.map(badge => {
        const cfg = BADGE_CONFIG[badge];
        if (!cfg) return null;
        return (
          <span key={badge} className={`inline-flex items-center gap-1 text-xs font-600 px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>
            <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={11} />
            {!compact && cfg.label}
          </span>
        );
      })}
      {compact && (badges || []).length > 2 && (
        <span className="text-xs text-muted-foreground">+{(badges || []).length - 2}</span>
      )}
    </div>
  );
}

// ─── Star Rating Display ──────────────────────────────────────────────────────
function StarRating({ rating, size = 12 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Icon
          key={star}
          name="StarIcon"
          size={size}
          className={star <= Math.round(rating) ? 'text-amber-500' : 'text-muted-foreground/30'}
        />
      ))}
    </div>
  );
}

// ─── Reviews Section ──────────────────────────────────────────────────────────
function ReviewsSection({ reviews, rating, reviews_count }: { reviews?: ServiceReview[]; rating: number; reviews_count: number }) {
  const [expanded, setExpanded] = useState(false);
  if (!reviews || reviews.length === 0) return null;

  const displayedReviews = expanded ? reviews : reviews.slice(0, 2);

  const ratingBreakdown = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => Math.round(r.rating) === star).length,
    pct: Math.round((reviews.filter(r => Math.round(r.rating) === star).length / reviews.length) * 100),
  }));

  return (
    <div className="mt-4 pt-4 border-t border-border">
      {/* Rating summary */}
      <div className="flex items-start gap-4 mb-4">
        <div className="text-center flex-shrink-0">
          <p className="text-3xl font-700 text-foreground leading-none">{rating.toFixed(1)}</p>
          <StarRating rating={rating} size={13} />
          <p className="text-xs text-muted-foreground mt-1">{reviews_count} reviews</p>
        </div>
        <div className="flex-1 space-y-1">
          {ratingBreakdown.map(({ star, count, pct }) => (
            <div key={star} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-3">{star}</span>
              <Icon name="StarIcon" size={10} className="text-amber-400 flex-shrink-0" />
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs text-muted-foreground w-4 text-right">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Individual reviews */}
      <div className="space-y-3">
        {displayedReviews.map(review => (
          <div key={review.id} className="bg-muted/50 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-600 flex-shrink-0">
                  {review.reviewer_initials}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-600 text-foreground">{review.reviewer_name}</span>
                    {review.verified_purchase && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full font-500">
                        <Icon name="CheckCircleIcon" size={10} />
                        Verified
                      </span>
                    )}
                  </div>
                  <StarRating rating={review.rating} size={10} />
                </div>
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0">{timeAgo(review.date)}</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{review.comment}</p>
          </div>
        ))}
      </div>

      {reviews.length > 2 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 w-full text-xs font-600 text-primary hover:text-primary/80 transition-colors py-1.5 border border-primary/20 rounded-lg hover:bg-primary/5"
        >
          {expanded ? 'Show less' : `View all ${reviews.length} reviews`}
        </button>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatsBar({ leads, ideas, services }: { leads: number; ideas: number; services: number }) {
  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      {[
        { label: 'Active Trade Leads', value: leads, icon: 'ArrowsRightLeftIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
        { label: 'Ideas Shared', value: ideas, icon: 'LightBulbIcon', color: 'text-amber-600', bg: 'bg-amber-50' },
        { label: 'Pro Services', value: services, icon: 'BriefcaseIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
      ].map(stat => (
        <div key={stat.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className={stat.color} />
          </div>
          <div>
            <p className="text-xl font-700 text-foreground">{stat.value}</p>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function TradeLeadCard({ lead, onMessage }: { lead: TradeLead; onMessage?: (lead: TradeLead) => void }) {
  const cfg = LEAD_TYPE_CONFIG[lead.lead_type];
  return (
    <div className="bg-card border border-border rounded-2xl p-5 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-xs font-600 px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>
              <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={12} />
              {cfg.label}
            </span>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{lead.category}</span>
          </div>
          <h3 className="text-sm font-600 text-foreground leading-snug">{lead.title}</h3>
        </div>
        {lead.budget_range && (
          <span className="text-xs font-600 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-lg whitespace-nowrap flex-shrink-0">
            {lead.budget_range}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">{lead.description}</p>

      {/* Seller profile row with verification */}
      <div className="flex items-center gap-2 mb-3 p-2.5 bg-muted/40 rounded-xl">
        <div className="w-7 h-7 rounded-full gradient-teal flex items-center justify-center text-white text-xs font-600 flex-shrink-0">
          {lead.avatar_initials || getInitials(lead.display_name || 'U')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-600 text-foreground">{lead.display_name}</span>
            {lead.verified && (
              <span className="inline-flex items-center gap-0.5 text-xs font-600 text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">
                <Icon name="CheckBadgeIcon" size={10} />
                Verified
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {lead.location && <span className="text-xs text-muted-foreground">{lead.location}</span>}
            {lead.member_since && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span className="text-xs text-muted-foreground">Member since {lead.member_since}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{timeAgo(lead.created_at)}</span>
        {onMessage && (
          <button
            onClick={() => onMessage(lead)}
            className="flex items-center gap-1 text-xs font-600 text-primary bg-primary/10 hover:bg-primary/20 px-2.5 py-1 rounded-lg transition-colors duration-150"
          >
            <Icon name="ChatBubbleLeftEllipsisIcon" size={13} />
            Message
          </button>
        )}
      </div>
    </div>
  );
}

function IdeaCard({ idea }: { idea: BusinessIdea }) {
  const stage = STAGE_CONFIG[idea.stage];
  return (
    <div className="bg-card border border-border rounded-2xl p-5 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full gradient-teal flex items-center justify-center text-white text-xs font-600 flex-shrink-0">
          {idea.avatar_initials || getInitials(idea.display_name || 'U')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${stage.bg} ${stage.color}`}>{stage.label}</span>
            <span className="text-xs text-muted-foreground">{idea.industry}</span>
          </div>
          <h3 className="text-sm font-600 text-foreground leading-snug">{idea.title}</h3>
        </div>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-3">{idea.summary}</p>
      {idea.seeking.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {idea.seeking.map(s => (
            <span key={s} className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">{s}</span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
          <Icon name="HandThumbUpIcon" size={14} />
          <span>{idea.upvotes}</span>
        </button>
        <span className="text-xs text-muted-foreground">{timeAgo(idea.created_at)}</span>
      </div>
    </div>
  );
}

function ServiceCard({
  service,
  onMessage,
  onSchedule,
  showReviews = false,
  availability = [],
}: {
  service: ServiceListing;
  onMessage?: (service: ServiceListing) => void;
  onSchedule?: (service: ServiceListing) => void;
  showReviews?: boolean;
  availability?: AvailabilitySlot[];
}) {
  const slotCount = availability.length;
  return (
    <div className="bg-card border border-border rounded-2xl p-5 hover:shadow-md transition-shadow duration-200">
      {/* Provider profile header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-full gradient-teal flex items-center justify-center text-white text-xs font-600 flex-shrink-0">
              {service.avatar_initials || getInitials(service.display_name || 'U')}
            </div>
            {service.verified && (
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center border-2 border-card">
                <Icon name="CheckIcon" size={8} className="text-white" />
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-xs font-600 text-foreground">{service.display_name}</p>
              {service.verified && (
                <span className="inline-flex items-center gap-0.5 text-xs font-600 text-blue-700">
                  <Icon name="CheckBadgeIcon" size={12} />
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{service.category}</p>
            {service.member_since && (
              <p className="text-xs text-muted-foreground">Member since {service.member_since}</p>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs font-700 text-emerald-700">{service.price_model}</p>
          <div className="flex items-center gap-1 justify-end mt-0.5">
            <StarRating rating={service.rating} size={11} />
            <span className="text-xs font-600 text-foreground">{service.rating}</span>
            <span className="text-xs text-muted-foreground">({service.reviews_count})</span>
          </div>
        </div>
      </div>

      {/* Verification badges */}
      {service.badges && service.badges.length > 0 && (
        <div className="mb-3">
          <VerificationBadges badges={service.badges} verified={service.verified} compact={!showReviews} />
        </div>
      )}

      <h3 className="text-sm font-600 text-foreground mb-1">{service.title}</h3>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">{service.description}</p>

      {/* Availability preview */}
      {slotCount > 0 && (
        <div className="flex items-center gap-1.5 mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Icon name="CalendarDaysIcon" size={13} className="text-emerald-600 flex-shrink-0" />
          <span className="text-xs font-500 text-emerald-700">{slotCount} slot{slotCount !== 1 ? 's' : ''} available this week</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          {service.tags.map(tag => (
            <span key={tag} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{tag}</span>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {onMessage && (
            <button
              onClick={() => onMessage(service)}
              className="flex items-center gap-1 text-xs font-600 text-primary bg-primary/10 hover:bg-primary/20 px-2.5 py-1 rounded-lg transition-colors duration-150"
            >
              <Icon name="ChatBubbleLeftEllipsisIcon" size={13} />
              Inquire
            </button>
          )}
          {onSchedule && (
            <button
              onClick={() => onSchedule(service)}
              className="flex items-center gap-1 text-xs font-600 text-white bg-primary hover:bg-primary/90 px-2.5 py-1 rounded-lg transition-colors duration-150"
            >
              <Icon name="CalendarDaysIcon" size={13} />
              Schedule
            </button>
          )}
        </div>
      </div>

      {/* Reviews section */}
      {showReviews && service.reviews && service.reviews.length > 0 && (
        <ReviewsSection reviews={service.reviews} rating={service.rating} reviews_count={service.reviews_count} />
      )}
    </div>
  );
}

function OverviewBento({ leads, ideas, services, onMessageLead, onMessageService, onScheduleService, availabilityMap }: {
  leads: TradeLead[];
  ideas: BusinessIdea[];
  services: ServiceListing[];
  onMessageLead: (lead: TradeLead) => void;
  onMessageService: (service: ServiceListing) => void;
  onScheduleService: (service: ServiceListing) => void;
  availabilityMap: Record<string, AvailabilitySlot[]>;
}) {
  return (
    <div className="space-y-6">
      {/* Hero bento */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Wide card */}
        <div className="md:col-span-2 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-primary/20 flex items-center justify-center">
              <Icon name="RocketLaunchIcon" size={18} className="text-primary" />
            </div>
            <span className="text-xs font-600 text-primary uppercase tracking-widest">Entrepreneur Hub</span>
          </div>
          <h2 className="text-xl font-700 text-foreground mb-2 leading-tight">Turn your original idea into a thriving business</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Connect with trade partners, access professional services, and share your business ideas with a community of Muslim entrepreneurs.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full font-500">Trade Leads</span>
            <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full font-500">Business Solutions</span>
            <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full font-500">Idea Validation</span>
            <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full font-500">Pro Services</span>
          </div>
        </div>
        {/* Tall quick-action card */}
        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-widest">Quick Actions</p>
          {[
            { label: 'Post a Trade Lead', icon: 'ArrowsRightLeftIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Share Your Idea', icon: 'LightBulbIcon', color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'List a Service', icon: 'BriefcaseIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Find a Partner', icon: 'UserGroupIcon', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          ].map(action => (
            <button key={action.label} className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors text-left w-full group">
              <div className={`w-8 h-8 rounded-lg ${action.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon name={action.icon as Parameters<typeof Icon>[0]['name']} size={16} className={action.color} />
              </div>
              <span className="text-sm font-500 text-foreground group-hover:text-primary transition-colors">{action.label}</span>
              <Icon name="ChevronRightIcon" size={14} className="ml-auto text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>

      {/* Latest leads */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-600 text-foreground">Latest Trade Leads</h3>
          <span className="text-xs text-primary cursor-pointer hover:underline">View all →</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {leads.slice(0, 2).map(lead => <TradeLeadCard key={lead.id} lead={lead} onMessage={onMessageLead} />)}
        </div>
      </div>

      {/* Ideas + Services row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-600 text-foreground">Top Ideas</h3>
            <span className="text-xs text-primary cursor-pointer hover:underline">View all →</span>
          </div>
          <div className="space-y-3">
            {ideas.slice(0, 2).map(idea => <IdeaCard key={idea.id} idea={idea} />)}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-600 text-foreground">Featured Services</h3>
            <span className="text-xs text-primary cursor-pointer hover:underline">View all →</span>
          </div>
          <div className="space-y-3">
            {services.slice(0, 2).map(svc => (
              <ServiceCard
                key={svc.id}
                service={svc}
                onMessage={onMessageService}
                onSchedule={onScheduleService}
                availability={availabilityMap[svc.id] || []}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function EntrepreneurHubPage() {
  const { user } = useAuth();
  const supabase = createClient();
  const router = useRouter();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [leads, setLeads] = useState<TradeLead[]>([]);
  const [ideas, setIdeas] = useState<BusinessIdea[]>([]);
  const [services, setServices] = useState<ServiceListing[]>([]);
  const [availabilityMap, setAvailabilityMap] = useState<Record<string, AvailabilitySlot[]>>(MOCK_AVAILABILITY);
  const [loading, setLoading] = useState(true);

  // Scheduling modal state
  const [schedulingService, setSchedulingService] = useState<ServiceListing | null>(null);
  const [lastConfirmed, setLastConfirmed] = useState<BookingConfirmation | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, ideasRes, servicesRes, availRes] = await Promise.all([
        supabase
          .from('entrepreneur_trade_leads')
          .select('*, user_profiles(full_name)')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('entrepreneur_ideas')
          .select('*, user_profiles(full_name)')
          .order('upvotes', { ascending: false })
          .limit(20),
        supabase
          .from('entrepreneur_services')
          .select('*, user_profiles(full_name)')
          .order('rating', { ascending: false })
          .limit(20),
        supabase
          .from('service_availability')
          .select('*')
          .eq('is_active', true),
      ]);

      if (leadsRes.data && leadsRes.data.length > 0) {
        setLeads(leadsRes.data.map((r: Record<string, unknown>) => ({
          ...r,
          display_name: (r.user_profiles as { full_name?: string } | null)?.full_name || 'Anonymous',
          avatar_initials: getInitials((r.user_profiles as { full_name?: string } | null)?.full_name || 'AN'),
        })) as TradeLead[]);
      } else {
        setLeads(MOCK_LEADS);
      }

      if (ideasRes.data && ideasRes.data.length > 0) {
        setIdeas(ideasRes.data.map((r: Record<string, unknown>) => ({
          ...r,
          display_name: (r.user_profiles as { full_name?: string } | null)?.full_name || 'Anonymous',
          avatar_initials: getInitials((r.user_profiles as { full_name?: string } | null)?.full_name || 'AN'),
        })) as BusinessIdea[]);
      } else {
        setIdeas(MOCK_IDEAS);
      }

      if (servicesRes.data && servicesRes.data.length > 0) {
        setServices(servicesRes.data.map((r: Record<string, unknown>) => ({
          ...r,
          display_name: (r.user_profiles as { full_name?: string } | null)?.full_name || 'Anonymous',
          avatar_initials: getInitials((r.user_profiles as { full_name?: string } | null)?.full_name || 'AN'),
        })) as ServiceListing[]);
      } else {
        setServices(MOCK_SERVICES);
      }

      if (availRes.data && availRes.data.length > 0) {
        const map: Record<string, AvailabilitySlot[]> = {};
        for (const slot of availRes.data as AvailabilitySlot[]) {
          if (!map[slot.service_id]) map[slot.service_id] = [];
          map[slot.service_id].push(slot);
        }
        setAvailabilityMap(map);
      }
    } catch {
      setLeads(MOCK_LEADS);
      setIdeas(MOCK_IDEAS);
      setServices(MOCK_SERVICES);
      setAvailabilityMap(MOCK_AVAILABILITY);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Message handlers ────────────────────────────────────────────────────
  const handleMessageLead = useCallback((lead: TradeLead) => {
    const params = new URLSearchParams({
      userName: lead.display_name || 'Entrepreneur',
      context: `trade_lead`,
      contextTitle: lead.title,
      contextType: lead.lead_type,
    });
    if (lead.user_id) params.set('userId', lead.user_id);
    router.push(`/messages?${params.toString()}`);
  }, [router]);

  const handleMessageService = useCallback((service: ServiceListing) => {
    const params = new URLSearchParams({
      userName: service.display_name || 'Service Provider',
      context: `service_inquiry`,
      contextTitle: service.title,
      contextType: 'service',
    });
    if (service.user_id) params.set('userId', service.user_id);
    router.push(`/messages?${params.toString()}`);
  }, [router]);

  const handleScheduleService = useCallback((service: ServiceListing) => {
    setSchedulingService(service);
  }, []);

  const handleBookingConfirmed = useCallback((booking: BookingConfirmation) => {
    setLastConfirmed(booking);
  }, []);

  return (
    <AppLayout activeRoute="/entrepreneur-hub">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="RocketLaunchIcon" size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-700 text-foreground">Entrepreneur Hub</h1>
              <p className="text-xs text-muted-foreground">Business solutions, trade leads & professional services</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {!loading && <StatsBar leads={leads.length} ideas={ideas.length} services={services.length} />}

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl mb-6 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-500 transition-all duration-150 whitespace-nowrap flex-1 justify-center ${
                activeTab === tab.value
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Tab Content */}
        {!loading && (
          <>
            {activeTab === 'overview' && (
              <OverviewBento
                leads={leads}
                ideas={ideas}
                services={services}
                onMessageLead={handleMessageLead}
                onMessageService={handleMessageService}
                onScheduleService={handleScheduleService}
                availabilityMap={availabilityMap}
              />
            )}

            {activeTab === 'trade-leads' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">{leads.length} active leads</p>
                  <button className="flex items-center gap-2 text-xs font-600 text-white bg-primary px-4 py-2 rounded-xl hover:bg-primary/90 transition-colors">
                    <Icon name="PlusIcon" size={14} />
                    Post Lead
                  </button>
                </div>
                {leads.length === 0 ? (
                  <div className="text-center py-16">
                    <Icon name="ArrowsRightLeftIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-600 text-foreground mb-1">No trade leads yet</p>
                    <p className="text-xs text-muted-foreground">Be the first to post a trade lead</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {leads.map(lead => <TradeLeadCard key={lead.id} lead={lead} onMessage={handleMessageLead} />)}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">{services.length} professional services</p>
                  <button className="flex items-center gap-2 text-xs font-600 text-white bg-primary px-4 py-2 rounded-xl hover:bg-primary/90 transition-colors">
                    <Icon name="PlusIcon" size={14} />
                    List Service
                  </button>
                </div>
                {services.length === 0 ? (
                  <div className="text-center py-16">
                    <Icon name="BriefcaseIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-600 text-foreground mb-1">No services listed yet</p>
                    <p className="text-xs text-muted-foreground">List your professional service to get started</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {services.map(svc => (
                      <ServiceCard
                        key={svc.id}
                        service={svc}
                        onMessage={handleMessageService}
                        onSchedule={handleScheduleService}
                        showReviews={true}
                        availability={availabilityMap[svc.id] || []}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'ideas' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">{ideas.length} ideas shared</p>
                  <button className="flex items-center gap-2 text-xs font-600 text-white bg-primary px-4 py-2 rounded-xl hover:bg-primary/90 transition-colors">
                    <Icon name="PlusIcon" size={14} />
                    Share Idea
                  </button>
                </div>
                {ideas.length === 0 ? (
                  <div className="text-center py-16">
                    <Icon name="LightBulbIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-600 text-foreground mb-1">No ideas shared yet</p>
                    <p className="text-xs text-muted-foreground">Share your original business idea with the community</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {ideas.map(idea => <IdeaCard key={idea.id} idea={idea} />)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Booking Modal */}
      {schedulingService && (
        <BookingModal
          service={schedulingService}
          availability={availabilityMap[schedulingService.id] || []}
          onClose={() => setSchedulingService(null)}
          onConfirm={handleBookingConfirmed}
        />
      )}
    </AppLayout>
  );
}
